import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, CheckCheck, Trash2, X, Info, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react'
import { clsx } from 'clsx'
import { useNotificationStore, type Notification } from '../../store/notificationStore'
import { formatDistanceToNow } from 'date-fns'
import { initPushNotifications, onForegroundMessage, onPushTokenRefresh } from '../../services/firebase'
import { apiPost } from '../../services/api'

const NOTIFICATION_ICONS: Record<string, React.ElementType> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
}

const NOTIFICATION_COLORS: Record<string, string> = {
  info: 'text-blue-500 bg-blue-50',
  success: 'text-green-500 bg-green-50',
  warning: 'text-yellow-500 bg-yellow-50',
  error: 'text-red-500 bg-red-50',
}

interface NotificationItemProps {
  notification: Notification
  onMarkRead: (id: number) => void
  onDelete: (id: number) => void
  onClick: (notification: Notification) => void
}

function NotificationItem({ notification, onMarkRead, onDelete, onClick }: NotificationItemProps) {
  const Icon = NOTIFICATION_ICONS[notification.notification_type] || Info
  const colorClass = NOTIFICATION_COLORS[notification.notification_type] || NOTIFICATION_COLORS.info

  return (
    <div
      className={clsx(
        'px-3 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0',
        !notification.is_read && 'bg-blue-50/30'
      )}
      onClick={() => onClick(notification)}
    >
      <div className="flex items-start gap-2">
        <div className={clsx('p-1.5 rounded-full flex-shrink-0', colorClass)}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-1">
            <p className={clsx(
              'text-xs font-medium text-gray-900 line-clamp-1',
              !notification.is_read && 'font-semibold'
            )}>
              {notification.title}
            </p>
            <div className="flex items-center flex-shrink-0">
              {!notification.is_read && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onMarkRead(notification.id)
                  }}
                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                  title="Mark as read"
                >
                  <Check className="h-3 w-3" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete(notification.id)
                }}
                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                title="Delete"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-0.5 line-clamp-2 leading-tight">
            {notification.message}
          </p>
          <p className="text-[10px] text-gray-400 mt-1">
            {formatDistanceToNow(new Date(notification.created_at.endsWith('Z') ? notification.created_at : notification.created_at + 'Z'), { addSuffix: true })}
          </p>
        </div>
      </div>
    </div>
  )
}

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const {
    notifications,
    unreadCount,
    isLoading,
    fetchRecentNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshUnreadCount,
  } = useNotificationStore()

  // Keep push as best-effort; do not prompt automatically from a notification bell mount.
  const initPush = useRef(false)
  useEffect(() => {
    if (initPush.current) return
    initPush.current = true

    void initPushNotifications({ requestPermission: false }).catch(() => {})
    const unsubscribeTokenRefresh = onPushTokenRefresh(token => {
      void apiPost('/notifications/fcm-token', { token })
        .then(() => localStorage.setItem('fcm_registered_token', token))
        .catch(() => {})
    })
    const unsubscribeForeground = onForegroundMessage(payload => {
      refreshUnreadCount()
      fetchRecentNotifications()
      const data = payload?.data || {}
      if (data.type === 'questionnaire-published' && data.id && 'Notification' in window && Notification.permission === 'granted') {
        const key = `questionnaire-alert:${data.id}`
        if (sessionStorage.getItem(key)) return
        sessionStorage.setItem(key, '1')
        new Notification(payload.notification?.title || data.title || 'New questionnaire', {
          body: payload.notification?.body || data.body || '',
          tag: `questionnaire-published-${data.id}`,
        })
      }
    })
    return () => {
      unsubscribeForeground()
      unsubscribeTokenRefresh()
    }
  }, [fetchRecentNotifications, navigate, refreshUnreadCount])

  // Fetch notifications on mount and periodically refresh
  useEffect(() => {
    fetchRecentNotifications()

    // Refresh unread count every 60 seconds
    const interval = setInterval(() => {
      refreshUnreadCount()
    }, 60000)

    return () => clearInterval(interval)
  }, [fetchRecentNotifications, refreshUnreadCount])

  // Refresh when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchRecentNotifications()
    }
  }, [isOpen, fetchRecentNotifications])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id)
    }
    setIsOpen(false)
    if (notification.link) {
      // Small delay to let dropdown close, then navigate
      setTimeout(() => navigate(notification.link!), 50)
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 flex items-center justify-center h-4 min-w-[1rem] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Mobile backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-40 sm:hidden"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown - responsive positioning */}
          <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-16 sm:top-auto sm:mt-2 sm:w-80 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold text-gray-900 text-sm">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <CheckCheck className="h-3.5 w-3.5" />
                    <span className="hidden xs:inline">Mark all read</span>
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Notification list */}
            <div className="max-h-[50vh] sm:max-h-[350px] overflow-y-auto">
              {isLoading && notifications.length === 0 ? (
                <div className="px-4 py-6 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-sm">Loading...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-6 text-center text-gray-500">
                  <Bell className="h-6 w-6 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkRead={markAsRead}
                    onDelete={deleteNotification}
                    onClick={handleNotificationClick}
                  />
                ))
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-3 py-2 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    setIsOpen(false)
                    navigate('/notifications')
                  }}
                  className="w-full text-center text-sm text-primary-600 hover:text-primary-800 font-medium py-1"
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
